import React from 'react'
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Badge,
  Fab,
  List,
  ListItem,
  Divider,
  Button,
  Chip,
} from '@mui/material'
import {
  Timeline as TimelineIcon,
  Close as CloseIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material'
import ProgressIndicator, { ProgressOperation } from './ProgressIndicator'

interface ProgressManagerProps {
  operations: ProgressOperation[]
  onClearCompleted: () => void
  onClearAll: () => void
  maxVisible?: number
}

const ProgressManager: React.FC<ProgressManagerProps> = ({
  operations,
  onClearCompleted,
  onClearAll,
  maxVisible = 5,
}) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false)

  const runningOperations = operations.filter(op => op.status === 'running')
  const completedOperations = operations.filter(op => op.status === 'completed')
  const errorOperations = operations.filter(op => op.status === 'error')
  const hasOperations = operations.length > 0

  const getOperationSummary = () => {
    if (runningOperations.length > 0) {
      return `${runningOperations.length} running`
    }
    if (errorOperations.length > 0) {
      return `${errorOperations.length} failed`
    }
    if (completedOperations.length > 0) {
      return `${completedOperations.length} completed`
    }
    return 'No operations'
  }

  const getBadgeCount = () => {
    return runningOperations.length + errorOperations.length
  }

  const getBadgeColor = () => {
    if (errorOperations.length > 0) return 'error'
    if (runningOperations.length > 0) return 'primary'
    return 'default'
  }

  // Show floating action button when there are operations
  if (!hasOperations) return null

  return (
    <>
      {/* Floating Action Button */}
      <Fab
        color="primary"
        onClick={() => setDrawerOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000,
        }}
      >
        <Badge
          badgeContent={getBadgeCount()}
          color={getBadgeColor() as any}
          max={99}
        >
          <TimelineIcon />
        </Badge>
      </Fab>

      {/* Progress Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 480,
            maxWidth: '90vw',
          }
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="h6">
                Operations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {getOperationSummary()}
              </Typography>
            </Box>
            <IconButton onClick={() => setDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={onClearCompleted}
              disabled={completedOperations.length === 0}
            >
              Clear Completed
            </Button>
            <Button
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onClearAll}
              color="error"
              disabled={runningOperations.length > 0}
            >
              Clear All
            </Button>
          </Box>

          {/* Summary Stats */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {runningOperations.length > 0 && (
              <Chip
                label={`${runningOperations.length} Running`}
                color="primary"
                size="small"
              />
            )}
            {completedOperations.length > 0 && (
              <Chip
                label={`${completedOperations.length} Completed`}
                color="success"
                size="small"
              />
            )}
            {errorOperations.length > 0 && (
              <Chip
                label={`${errorOperations.length} Failed`}
                color="error"
                size="small"
              />
            )}
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Operations List */}
          <Box sx={{ maxHeight: 'calc(100vh - 200px)', overflow: 'auto' }}>
            {operations.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <TimelineIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No operations running
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {operations.map((operation) => (
                  <ListItem key={operation.id} sx={{ p: 0, mb: 2 }}>
                    <ProgressIndicator
                      operation={operation}
                      variant="detailed"
                      showSteps={true}
                      autoClose={false}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Inline Progress for Running Operations */}
      {runningOperations.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            top: 80,
            right: 24,
            width: 320,
            maxHeight: 400,
            overflow: 'auto',
            zIndex: 999,
          }}
        >
          {runningOperations.slice(0, maxVisible).map((operation) => (
            <ProgressIndicator
              key={operation.id}
              operation={operation}
              variant="compact"
              showSteps={false}
              autoClose={false}
            />
          ))}
          {runningOperations.length > maxVisible && (
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Button
                size="small"
                onClick={() => setDrawerOpen(true)}
              >
                +{runningOperations.length - maxVisible} more operations
              </Button>
            </Box>
          )}
        </Box>
      )}
    </>
  )
}

export default ProgressManager